import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Keyboard } from 'react-native';
import { HelperText, useTheme } from 'react-native-paper';
import { times } from 'lodash';
import styled from 'styled-components/native';

type Props = {
  name: string;
  score: number;
  children: React.ReactElement;
  label?: string;
  color?: string;
  max?: number;
  compact?: boolean;
  rtl?: boolean;
};

const Row = styled.View`
  flex-direction: row;
  align-items: center;
  flex-grow: 1;
  justify-content: center;
`;

const Touchable = styled.TouchableOpacity`
  margin: 1px auto;
`;

const Component: React.FC<Props> = (props) => {
  const { name, children, score, color, max = 5, compact, rtl = true } = props;
  const { register, unregister, errors, setValue, watch } = useFormContext();
  const theme = useTheme();
  const error = errors?.[name];
  const value = watch(name, score);

  React.useEffect(() => {
    register(name);
    return () => unregister(name);
  }, [name, register, unregister]);

  const onChildPressHandler = (inputValue: number) => {
    Keyboard.dismiss();
    setValue(name, inputValue);
  };

  const flexDirection = React.useMemo(() => (rtl ? 'row-reverse' : 'row'), [
    rtl,
  ]);

  if (!React.isValidElement(children)) {
    return null;
  }

  return (
    <>
      <Row style={{ flexDirection }}>
        {times(max).map((index) => (
          <Touchable key={index} onPress={() => onChildPressHandler(index + 1)}>
            {index + 1 <= value
              ? React.cloneElement(children, {
                  style: { color: color ?? theme.colors.primary },
                })
              : React.cloneElement(children, {
                  style: { color: theme.colors.placeholder },
                })}
          </Touchable>
        ))}
      </Row>
      {!compact && (
        <HelperText type="error" visible={!!error}>
          {error?.message}
        </HelperText>
      )}
    </>
  );
};

Component.displayName = 'ScoreInput';

export default React.memo(Component);
